import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold outline-none transition-all active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
  {
    variants: {
      variant: {
        // Solid coral primary CTA. Hover deepens to --primary-hover
        // (a real darker step, not an alpha fade).
        default: "bg-primary text-primary-foreground shadow-brand-glow hover:bg-primary-hover",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        // 1px-bordered ghost — hover fills the teal accent wash.
        outline:
          "border border-input bg-card shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-accent-border dark:bg-input/30",
        // Quiet neutral fill — recedes so the coral primary clearly wins (one
        // loud action per view; Refactoring UI button hierarchy).
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline active:translate-y-0",
      },
      size: {
        // Joe-aligned geometry (CHP-522): taller, rounder CTAs — default 40 /
        // sm 32 / lg 44, icon square to match.
        default: "h-10 px-5",
        sm: "h-8 px-3.5 text-sm",
        lg: "h-11 px-6",
        icon: "size-10",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Show a spinner and disable the button while an action is in flight (CHP-522). */
  loading?: boolean;
  /** Label shown while loading (defaults to the button's children). */
  loadingLabel?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingLabel,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    // asChild forwards to a single child (e.g. a <Link>); the spinner only
    // applies to a real <button> so we don't break the Slot single-child rule.
    const showSpinner = loading && !asChild;
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || showSpinner}
        aria-busy={loading || undefined}
        {...props}
      >
        {showSpinner ? (
          <>
            <Loader2 className="animate-spin" aria-hidden />
            {loadingLabel ?? children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
